/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'next-i18next';
import {
  FC, useCallback, useEffect, useMemo,
} from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import Dropdown, { DropdownOption } from 'src/common/components/DropdownDS22';
import TimeInput from 'src/common/components/Input/TimeInput';
import { FORM_DEPARTURE_TIME } from 'src/inquiry/constants';
import { TripCartFormState } from 'src/listing/types';
import { useTripCartOptionsContext } from 'src/trip-cart/hooks/useTripCartOptionsContext';
import { formatDate } from 'src/common/utils/dateHelpers';
import { TIME_24 } from 'src/common/constants';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import Label from 'src/common/components/Label';
import { roundedDepartureTime } from 'src/trip-cart/hooks/useTripCartOptionsContext/getTripCartOptions';
import InputError from 'src/admin/common/components/InputError';
import TripCartAttention from 'src/trip-cart/components/TripCartAttention';
import { useTripCartSeasonsContext } from 'src/trip-cart/hooks/useTripCartSeasonsContext';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import styles from './TripCartDepartureTime.module.scss';

const DEFAULT_TIME = '09:00';

const TripCartDepartureTime: FC = () => {
  const [t] = useTranslation('listing-page');

  const { change } = useForm<TripCartFormState>();
  const {
    values: {
      departureTime,
      isFormStateValid,
    }, submitErrors, dirtyFieldsSinceLastSubmit,
  } = useFormState<TripCartFormState>();
  const { addAttentionField, options: { errors } } = useTripCartOptionsContext();
  const { departureTimesForDate } = useTripCartSeasonsContext();
  const { hasDepartureTimes } = useTripCartContext();
  const timeOptions = useMemo((): DropdownOption[] | undefined => {
    if (!isFormStateValid || !hasDepartureTimes) {
      return [{
        id: '',
        selectedLabel: '...',
        dropdownLabel: '...',
        icon: undefined,
      }];
    }
    return departureTimesForDate?.map(({ formattedTime24 }) => ({
      id: formattedTime24,
      selectedLabel: formattedTime24,
      dropdownLabel: formattedTime24,
    }));
  }, [departureTimesForDate, isFormStateValid, hasDepartureTimes]);

  const locale = useDateFnsLocale();
  const formatTime = useCallback((value: Date) => formatDate(value, TIME_24, locale), []);

  const onFocus = useCallback(() => {
    if (!departureTime) {
      change(FORM_DEPARTURE_TIME, DEFAULT_TIME);
    }
  }, [departureTime, change]);

  const onBlur = useCallback(() => {
    if (!departureTime) {
      return;
    }
    const value = roundedDepartureTime(departureTime);
    const formattedValue = formatTime(value);
    if (formattedValue !== departureTime) {
      addAttentionField('departureTime');
    }
    change(FORM_DEPARTURE_TIME, formattedValue);
  }, [change, departureTime]);

  const onChangeDropdown = useCallback((option: DropdownOption) => {
    change(FORM_DEPARTURE_TIME, option.selectedLabel);
  }, [change]);

  const selectedOption = useMemo(() => (
    !isFormStateValid || !hasDepartureTimes
      ? timeOptions?.[0]
      : timeOptions?.find(({ selectedLabel }) => (
        departureTime === selectedLabel
      ))), [timeOptions, departureTime, isFormStateValid]);

  useEffect(() => {
    // Force update the field when errors trigger
    if (departureTime && errors?.departureTime && timeOptions?.length) {
      change('departureTime', undefined);
    }
  }, [departureTime, errors?.departureTime]);

  let errorMessage = '';
  // local error takes precedence
  if (hasDepartureTimes && errors?.departureTime) {
    errorMessage = errors.departureTime;
  } else if (submitErrors?.departureTime) {
    errorMessage = t(submitErrors?.departureTime);
  }
  const hasError = !!(
    (
      !dirtyFieldsSinceLastSubmit.departureTime
      && submitErrors?.departureTime
    ) || errors?.departureTime
  );

  return (
    <div className={styles.root}>
      {timeOptions ? (
        <Label
          label={t('trip-cart--departure-time')}
          htmlFor={FORM_DEPARTURE_TIME}
          required
          size="m"
        >
          <Field
            name={FORM_DEPARTURE_TIME}
          >
            {() => (
              <div className={styles.field}>
                <TripCartAttention field="departureTime" />
                <Dropdown
                  id={FORM_DEPARTURE_TIME}
                  instructionLabel={t('trip-cart--departure-time-placeholder')}
                  variant="white"
                  options={timeOptions}
                  selectedValue={selectedOption}
                  onChange={onChangeDropdown}
                  limitMenuHeight
                  error={hasError}
                  optionSize="l"
                  buttonSize="l"
                  selectedIconId="clock"
                  renderInPortal
                  fullWidth
                  disabled={!isFormStateValid || !hasDepartureTimes}
                  disabledBackground="grey"
                />
                {hasError && (
                  <InputError preventLayoutShift={false}>
                    {errorMessage}
                  </InputError>
                )}
              </div>
            )}
          </Field>
        </Label>
      ) : (
        <div className={styles.field}>
          <TimeInput
            name={FORM_DEPARTURE_TIME}
            label={t('trip-cart--departure-time')}
            labelSize="m"
            fullWidth
            invalidMessage={hasError ? errorMessage : ''}
            onFocus={onFocus}
            onBlur={onBlur}
            onInvalid={onBlur}
            required
            preventLayoutShift={false}
          >
            <TripCartAttention field="departureTime" />
          </TimeInput>
        </div>
      )}
    </div>
  );
};

export default TripCartDepartureTime;
